import './SalesBanner.scss';

import React, { useState, useRef, useEffect } from 'react';

import { FlexBox, P, Typography } from '@vp/swan';

import { TextBlockModel } from '@vp/digital-common-data-model-lib';

import MissingContentAlert from '../MissingContentAlert';
import useSeasonalSales, {
  formatRemainingTime,
} from '../hooks/useSeasonalSales';

type SalesBannerProps = {
  content: TextBlockModel;
  locale: string;
};

const SalesBanner = ({ content, locale }: SalesBannerProps) => {
  const { isSeasonalSales, endDateTime, remainingTimestamp } =
    useSeasonalSales();

  if (!content) {
    return <MissingContentAlert />;
  }

  if (isSeasonalSales) {
    return (
      <Banner
        content={content}
        isSeasonalSales={isSeasonalSales}
        endDateTime={endDateTime}
        remainingTimestamp={remainingTimestamp}
        locale={locale}
      />
    );
  }

  return <React.Fragment />;
};

type BannerProps = {
  content: TextBlockModel;
  isSeasonalSales: Boolean;
  endDateTime: Date;
  remainingTimestamp: number;
  locale: string;
};

const Banner = ({
  content,
  isSeasonalSales,
  endDateTime,
  remainingTimestamp,
  locale,
}: BannerProps) => {
  const interval = useRef(undefined);
  const [remainingSalesTime, setRemainingSalesTime] = useState('');

  let remainingTimestampSeconds = remainingTimestamp;

  useEffect(() => {
    const startCountDown = () => {
      clearInterval(interval.current);
      setRemainingSalesTime(formatRemainingTime(remainingTimestampSeconds));

      interval.current = setInterval(() => {
        remainingTimestampSeconds--;
        setRemainingSalesTime(formatRemainingTime(remainingTimestampSeconds));
      }, 1000);
    };

    if (isSeasonalSales && content) {
      startCountDown();
    } else {
      clearInterval(interval.current);
    }

    return () => clearInterval(interval.current);
  }, [isSeasonalSales, content, remainingTimestampSeconds]);

  return (
    <FlexBox
      alignItems="center"
      justifyContent="center"
      gap={1}
      fontWeight="bold"
      fontSize="small"
      p="2"
      backgroundColor="black"
      className="wix-websites-pdp__sales-banner__container"
    >
      <P my={0} textAlign={'center'} px={5}>
        <Typography component="span" darkMode>
          {content?.description}{' '}
          {`${endDateTime.toLocaleDateString(locale + '', {
            month: 'long',
            day: 'numeric',
          })}, 
                ${endDateTime.toLocaleTimeString(locale + '', {
                  hour: 'numeric',
                })}`}
        </Typography>
        <Typography
          component="span"
          darkMode={false}
          marginLeft={3}
          paddingX={3}
          paddingY={2}
          backgroundColor="warm-white"
          className="wix-websites-pdp__sales-banner__sales-remaining-time"
        >
          {remainingSalesTime}
        </Typography>
      </P>
    </FlexBox>
  );
};

export default SalesBanner;
